import { z } from 'zod';
import { zGoDate, zGoNullableDate, zGoSlice } from '../helpers/fix-go-types';
import { AddressSchema } from './address';
import { StoreStatsSchema } from './store-stats';

const MilestoneFlagsSchema = z.object({
  uploaded_data: z.boolean(),
  vendor_integrated: z.boolean(),
  sent_first_order: z.boolean(),
  received_first_order: z.boolean(),
  trial_converted: z.boolean(),
});

export type MilestoneFlags = z.infer<typeof MilestoneFlagsSchema>;

const LabelSchema = z.object({
  id: z.number(),
  label: z.string(),
  color: z.string(),
});

export type Label = z.infer<typeof LabelSchema>;

export enum UserRole {
  Owner = 'owner',
  Manager = 'operator',
  Employee = 'employee',
  Demo = 'demo',
}

export const UserRoleLabel: Record<string, string> = {
  [UserRole.Owner]: 'Owner',
  [UserRole.Manager]: 'Manager',
  [UserRole.Employee]: 'Employee',
  [UserRole.Demo]: 'Demo',
};

const UserStoreRelationSchema = z.object({
  email: z.email(),
  store_id: z.string(),
  relation: z.enum(UserRole),
});

export type UserStoreRelation = z.infer<typeof UserStoreRelationSchema>;

export const StoreSchema = z.object({
  id: z.string(),
  product_key: z.string(),
  owner_first_name: z.string(),
  owner_last_name: z.string(),
  phone_number: z.string(),
  billing_address: AddressSchema,
  shipping_address: AddressSchema,
  // The wire carries `timezone` with no omitempty, so an unset one arrives as
  // '' rather than absent. Both fall through to the browser's zone; the
  // transform runs before the default, exactly as the Yup version did.
  timezone: z
    .string()
    .optional()
    .transform((value) => value || Intl.DateTimeFormat().resolvedOptions().timeZone),
  name: z.string(),
  enabled: z.boolean(),
  subscription_id: z.string().default(''),

  milestones: MilestoneFlagsSchema,
  trial_end: zGoDate.optional(),

  stats: StoreStatsSchema,
  // Go serializes a nil `[]Label` (no omitempty) as `null`; zGoSlice coerces
  // null → [] so a store with no labels isn't dropped by validation.
  labels: zGoSlice(LabelSchema),
  // `Users` DOES carry omitempty (models/stores.go:38), so an empty set is an
  // absent key rather than null — which a bare required array rejected, taking
  // the whole store with it. The store-edit form's `store.users || []` reads
  // like somebody met this. zGoSlice covers absent and null alike.
  users: zGoSlice(UserStoreRelationSchema),

  created_at: zGoDate,
  updated_at: zGoDate,
  last_uploaded_at: zGoNullableDate,

  // The operations this user may perform on this store, computed server-side
  // by the same table that enforces them (see data/permissions.ts). Optional
  // for the same reason as `features` below.
  //
  // Deliberately NOT zGoSlice, unlike `labels` and `users` above: coercing
  // null/absent to [] would collapse "the server didn't say" into "the server
  // said nothing is allowed", and `can()` resolves those two opposite ways.
  permissions: z.array(z.string()).nullish(),

  // Computed server-side from feature flags. Optional so an absent field
  // (old backend during deploy, or IndexedDB rehydration that bypasses the
  // schema) is undefined and isReceivingEnabled can fall back to the
  // hardcoded list.
  features: z
    .object({
      web_receiving: z.boolean().default(false),
    })
    .optional(),
});

export type Store = z.infer<typeof StoreSchema>;

export const isTestStore = (store?: Store) => {
  if (!store) {
    return false;
  }

  return store.product_key.startsWith('N2L8-RYD');
};

export const isMarysStore = (store?: Store) => {
  if (!store) {
    return false;
  }

  return store.product_key === '6T5K-K2Y7';
};

export const isDemoStore = (store?: Store) => {
  if (!store) {
    return false;
  }

  return store.product_key === 'W8UO-EYR6';
};

// trial_end is set at signup and cleared by the first paid invoice, so a stale
// one (webhook missed) must not keep a paying store in the trial: compare to now.
export const isTrialing = (store: Store, now: Date = new Date()) =>
  store.trial_end != null && store.trial_end > now;
